/* eslint-disable no-lonely-if */
import validator from 'validator';
import isEmptyObj from 'lodash/isEmpty';
import moment from 'logic/moment';
import { normalizeIcalUrls } from 'logic/cottage';

const isEmpty = (str) => {
  if (!str) {
    return true;
  }
  if (typeof str === 'string' && validator.isEmpty(str.trim())) {
    return true;
  }
  return false;
};

// Backend caps a booking at 100 days (api booking validator). Calendar-day diff
// so a same-day time-of-day difference can't push a valid stay over the edge.
export const MAX_STAY_DAYS = 100;
export const exceedsMaxStay = (check_in, check_out) =>
  check_in.isValid() &&
  check_out.isValid() &&
  check_out.clone().startOf('day').diff(check_in.clone().startOf('day'), 'days') > MAX_STAY_DAYS;

export const validateEdit = (values, props) => {
  const errors = {};

  if (isEmpty(values.booking_expiration_date)) {
    errors.booking_expiration_date = props.intl.formatMessage({
      id: 'bookingForm.requiredExpDate',
      defaultMessage: 'Booking expiration date is required',
    });
  }

  return errors;
};

export const validateEditPatch = (props) => (values) => {
  return validateEdit(values, props);
};

export const validateHostOfferEdit = (values, props) => {
  const errors = {};
  // Calendar-day boundaries so a same-day check-in with a later time of day
  // can't sneak through a raw isAfter(timestamp) comparison.
  const todayStart = moment().startOf('day');
  const tomorrowStart = todayStart.clone().add(1, 'day');
  const after2YearsEnd = moment().add(2, 'years').endOf('day');
  const check_in = moment(values.check_in);

  if (isEmpty(values.check_in)) {
    errors.check_in = props.intl.formatMessage({
      id: 'bookingForm.requiredCheckIn',
      defaultMessage: 'Check in date is required',
    });
  } else if (!check_in.isValid()) {
    errors.check_in = props.intl.formatMessage({
      id: 'bookingForm.invalidDate',
      defaultMessage: 'Invalid date',
    });
  } else if (check_in.isBefore(tomorrowStart)) {
    errors.check_in = props.intl.formatMessage({
      id: 'bookingForm.lessCheckIn',
      defaultMessage: 'Check in date should start from tomorrow or later',
    });
  } else if (check_in.isAfter(after2YearsEnd)) {
    errors.check_in = props.intl.formatMessage({
      id: 'bookingForm.farCheckIn',
      defaultMessage: 'Check in is too far in future',
    });
  }

  if (isEmpty(values.check_out)) {
    errors.check_out = props.intl.formatMessage({
      id: 'bookingForm.requiredCheckOut',
      defaultMessage: 'Check out date is required',
    });
  } else {
    const check_out = moment(values.check_out);
    if (!check_out.isValid()) {
      errors.check_out = props.intl.formatMessage({
        id: 'bookingForm.invalidDate',
        defaultMessage: 'Invalid date',
      });
    } else if ((check_in && check_in.isSameOrAfter(check_out)) || check_out.isBefore(todayStart)) {
      errors.check_out = props.intl.formatMessage({
        id: 'bookingForm.lessCheckOut',
        defaultMessage: 'Check out date should be greater than check in',
      });
    } else if (!check_out.isAfter(check_in, 'day')) {
      errors.check_out = props.intl.formatMessage({
        id: 'bookingForm.min1DayStay',
        defaultMessage: 'Check out should be at least 1 day after check in',
      });
    } else if (exceedsMaxStay(check_in, check_out)) {
      errors.check_out = props.intl.formatMessage({
        id: 'bookingForm.maxStayDays',
        defaultMessage: 'Stay can not exceed 100 days',
      });
    } else if (check_out.isAfter(after2YearsEnd)) {
      errors.check_out = props.intl.formatMessage({
        id: 'bookingForm.farCheckOut',
        defaultMessage: 'Check out is too far in future',
      });
    }
  }

  if (isEmpty(values.total_price)) {
    errors.total_price = props.intl.formatMessage({
      id: 'bookingForm.requiredTotalPrice',
      defaultMessage: 'Total price is required',
    });
  } else if (!/^[0-9]+$/.test(String(values.total_price))) {
    errors.total_price = props.intl.formatMessage({
      id: 'bookingForm.invalidTotalPrice',
      defaultMessage: 'Invalid value',
    });
  } else if (values.total_price < 100 || values.total_price > 100000) {
    errors.total_price = props.intl.formatMessage(
      {
        id: 'bookingForm.totalPriceRange',
        defaultMessage: 'Must be between {min} and {max}',
      },
      {
        min: '100',
        max: '100 000',
      },
    );
  }

  if (isEmpty(values.booking_expiration_date)) {
    errors.booking_expiration_date = props.intl.formatMessage({
      id: 'bookingForm.requiredExpDate',
      defaultMessage: 'Booking expiration date is required',
    });
  } else {
    const expiration = moment(values.booking_expiration_date);
    if (!expiration.isValid()) {
      errors.booking_expiration_date = props.intl.formatMessage({
        id: 'bookingForm.invalidDate',
        defaultMessage: 'Invalid date',
      });
    } else if (check_in.isValid() && expiration.isAfter(check_in, 'day')) {
      // Day-granularity match: backend accepts same-day expiration (caps at
      // check_in.endOf('day')) and setExpirationDateTime normalises to end of
      // day on submit, so the picker's lingering time-of-day must not block
      // a valid same-day pick.
      errors.booking_expiration_date = props.intl.formatMessage({
        id: 'bookingForm.expirationAfterCheckIn',
        defaultMessage: 'Expiration must be on or before check in',
      });
    }
  }

  return errors;
};

export const validateHostOfferEditPatch = (props) => (values) =>
  validateHostOfferEdit(values, props);

export const validate = (values, props) => {
  const errors = {};
  if (isEmpty(values.cottage_id)) {
    errors.cottage_id = props.intl.formatMessage({
      id: 'bookingForm.requiredCottage',
      defaultMessage: 'Cottage is required',
    });
  }

  const today = moment();
  const after2Years = moment().add(2, 'years');
  const check_in = moment(values.check_in);

  if (isEmpty(values.check_in)) {
    errors.check_in = props.intl.formatMessage({
      id: 'bookingForm.requiredCheckIn',
      defaultMessage: 'Check in date is required',
    });
  } else {
    if (!check_in.isValid()) {
      errors.check_in = props.intl.formatMessage({
        id: 'bookingForm.invalidDate',
        defaultMessage: 'Invalid date',
      });
    } else {
      if (today.isAfter(check_in)) {
        errors.check_in = props.intl.formatMessage({
          id: 'bookingForm.lessCheckIn',
          defaultMessage: 'Check in date should start from tomorrow or later',
        });
      } else if (check_in.isAfter(after2Years)) {
        errors.check_in = props.intl.formatMessage({
          id: 'bookingForm.farCheckIn',
          defaultMessage: 'Check in is too far in future',
        });
      }
    }
  }

  if (isEmpty(values.check_out)) {
    errors.check_out = props.intl.formatMessage({
      id: 'bookingForm.requiredCheckOut',
      defaultMessage: 'Check out date is required',
    });
  } else {
    const check_out = moment(values.check_out);
    if (!check_out.isValid()) {
      errors.check_out = props.intl.formatMessage({
        id: 'bookingForm.invalidDate',
        defaultMessage: 'Invalid date',
      });
    } else {
      if ((check_in && check_in.isSameOrAfter(check_out)) || today.isAfter(check_out)) {
        errors.check_out = props.intl.formatMessage({
          id: 'bookingForm.lessCheckOut',
          defaultMessage: 'Check out date should be greater than check in',
        });
      } else if (!check_out.isAfter(check_in, 'day')) {
        errors.check_out = props.intl.formatMessage({
          id: 'bookingForm.min1DayStay',
          defaultMessage: 'Check out should be at least 1 day after check in',
        });
      } else if (exceedsMaxStay(check_in, check_out)) {
        errors.check_out = props.intl.formatMessage({
          id: 'bookingForm.maxStayDays',
          defaultMessage: 'Stay can not exceed 100 days',
        });
      } else if (check_out.isAfter(after2Years)) {
        errors.check_out = props.intl.formatMessage({
          id: 'bookingForm.farCheckOut',
          defaultMessage: 'Check out is too far in future',
        });
      }
    }
  }

  if (isEmpty(values.number_of_guests)) {
    errors.number_of_guests = props.intl.formatMessage({
      id: 'bookingForm.requiredNoOfGuests',
      defaultMessage: 'Number of guests is required',
    });
  } else if (!validator.isInt(String(values.number_of_guests), { min: 1, max: 100 })) {
    errors.number_of_guests = props.intl.formatMessage({
      id: 'bookingForm.invalidNoOfGuests',
      defaultMessage: 'Out of range',
    });
  }

  if (isEmpty(values.cancellation_policy_id)) {
    errors.cancellation_policy_id = props.intl.formatMessage({
      id: 'bookingForm.requiredCancelPolicy',
      defaultMessage: 'Cancellation policy is required',
    });
  }

  if (isEmpty(values.cleaning_policy)) {
    errors.cleaning_policy = props.intl.formatMessage({
      id: 'bookingForm.requiredCleanPolicy',
      defaultMessage: 'Cleaning policy is required',
    });
  }

  if (isEmpty(values.total_price)) {
    errors.total_price = props.intl.formatMessage({
      id: 'bookingForm.requiredTotalPrice',
      defaultMessage: 'Total price is required',
    });
  } else if (!/^[0-9]+?$/.test(values.total_price)) {
    errors.total_price = props.intl.formatMessage({
      id: 'bookingForm.invalidTotalPrice',
      defaultMessage: 'Invalid value',
    });
  } else if (values.total_price < 100 || values.total_price > 100000) {
    errors.total_price = props.intl.formatMessage(
      {
        id: 'bookingForm.totalPriceRange',
        defaultMessage: 'Must be between {min} and {max}',
      },
      {
        min: '100',
        max: '100 000',
      },
    );
  }

  if (isEmpty(values.booking_expiration_date)) {
    errors.booking_expiration_date = props.intl.formatMessage({
      id: 'bookingForm.requiredExpDate',
      defaultMessage: 'Booking expiration date is required',
    });
  }

  return errors;
};

export const validateNewPatch = (props) => (values) => {
  return validate(values, props);
};

export const getMinMaxDates = () => ({
  min: `${moment().format('YYYY-MM-DD')}`,
  max: `${moment().add(2, 'years').format('YYYY-MM-DD')}`,
});

// A cottage is "ical-synced" when external sync is on AND at least one url is
// configured. With sync active the external calendar owns availability, so the
// auto-mark-as-booked option must not be offered (STU-258).
export const cottageIsIcalSynced = (cottage) =>
  Boolean(cottage && cottage.ical_sync) && normalizeIcalUrls(cottage.ical_url).length > 0;

export const setExpirationDateTime = (expiration, checkIn) => {
  const expMoment = moment(expiration);
  if (checkIn) {
    const checkInMoment = moment(checkIn);
    if (checkInMoment.isValid() && expMoment.isSame(checkInMoment, 'day')) {
      // Same calendar day as check_in: cap at check_in - 1s so expiration is
      // strictly before check_in, not end-of-day. Backend would accept end of
      // day (cap is check_in.endOf('day')) but a guest deciding *after* the
      // host expects them on the cottage makes no business sense.
      return checkInMoment.clone().subtract(1, 'second').toISOString();
    }
  }
  return expMoment.endOf('day').toISOString();
};

export const cleanUpData = (data) => {
  const cleanData = {
    conversation_id: data.conversation_id,
    guest_id: data.guest_id,
    cottage_id: data.cottage_id,
    cancellation_policy_id: data.cancellation_policy_id,
    check_in: moment(data.check_in).toISOString(),
    check_out: moment(data.check_out).toISOString(),
    number_of_guests: data.number_of_guests,
    cleaning_policy: data.cleaning_policy,
    total_price: Number(data.total_price) * 100,
    booking_expiration_date: setExpirationDateTime(data.booking_expiration_date, data.check_in),
    mark_as_booked_if_accepted: data.mark_as_booked_if_accepted,
  };
  if (data.additional_information && data.additional_information.trim() !== '') {
    cleanData.additional_information = data.additional_information;
  }
  return cleanData;
};

export const bookingActionValidate = (values, props) => {
  const errors = {};
  if (isEmpty(values.cancellation_reason)) {
    errors.cancellation_reason = props.intl.formatMessage({
      id: 'bookingForm.Pleasespecifyareason',
      defaultMessage: 'Please specify a reason.',
    });
  }
  if (isEmptyObj(errors) && !values.confirm) {
    errors.confirm = props.intl.formatMessage({
      id: 'bookingForm.Confirmcancellation',
      defaultMessage: 'Confirm cancellation',
    });
  }
  return errors;
};
